import { useEffect, useRef, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { useSite } from '../lib/SiteContentContext';

// easeOutCubic
const ease = (t) => 1 - Math.pow(1 - t, 3);

/** Animates a numeric value from 0 → target over `duration` when `start` flips true. */
function useCountUp(target, duration = 1600, start = false, decimals = 0) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const t0 = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      setValue(target * ease(t));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return decimals === 0 ? Math.round(value) : Number(value.toFixed(decimals));
}

/** Fires once when the element scrolls into view. */
function useInView(threshold = 0.35) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current || inView) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setInView(true); });
      },
      { threshold }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [inView, threshold]);
  return [ref, inView];
}

function ComparisonBars({ item, inView }) {
  // Two-frame delay ensures the browser paints the 0% state before
  // committing the target width, so the CSS transition actually plays.
  const [grow, setGrow] = useState(false);
  useEffect(() => {
    if (!inView) return;
    const r1 = requestAnimationFrame(() => {
      const r2 = requestAnimationFrame(() => setGrow(true));
      return () => cancelAnimationFrame(r2);
    });
    return () => cancelAnimationFrame(r1);
  }, [inView]);

  // Numbers count up in perfect sync with the bar growth.
  const us = useCountUp(item.us, 1800, grow, 1);
  const nat = useCountUp(item.national, 1800, grow, 1);

  const barTransition = 'width 1800ms cubic-bezier(0.33, 1, 0.68, 1)';
  const usWidth = grow ? `${item.us}%` : '0%';
  const natWidth = grow ? `${item.national}%` : '0%';

  return (
    <div className="rounded-3xl bg-white/5 border border-white/10 p-5 md:p-6">
      <div className="flex items-baseline justify-between">
        <div className="font-heading text-lg md:text-xl uppercase tracking-wider text-brand-sun">
          {item.label}
        </div>
        <div className="text-xs uppercase tracking-widest text-brand-cream/70">{item.metric}</div>
      </div>

      {/* Science Sorted bar */}
      <div className="mt-5">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-semibold text-brand-cream">Science Sorted</span>
          <span className="font-display text-2xl md:text-3xl text-brand-sun">{us.toFixed(1)}%</span>
        </div>
        <div className="mt-2 h-6 md:h-7 rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full rounded-full bg-brand-sun"
            style={{ width: usWidth, transition: barTransition }}
            aria-hidden="true"
          />
        </div>
      </div>

      {/* National bar */}
      <div className="mt-4">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-semibold text-brand-cream/85">National average</span>
          <span className="font-display text-xl md:text-2xl text-brand-cream/80">{nat.toFixed(1)}%</span>
        </div>
        <div className="mt-2 h-6 md:h-7 rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full rounded-full bg-white"
            style={{ width: natWidth, transition: barTransition }}
            aria-hidden="true"
          />
        </div>
      </div>

      <div className="mt-4 text-xs text-brand-cream/60">
        {(item.us - item.national).toFixed(1)} percentage points above the national average.
      </div>
    </div>
  );
}

export default function StatsBand() {
  const { results } = useSite();
  const [ref, inView] = useInView(0.25);

  // Same two-frame guard so the "+N grades" number counts up in lockstep
  // with the comparison bars beginning their width transition.
  const [grow, setGrow] = useState(false);
  useEffect(() => {
    if (!inView) return;
    const r1 = requestAnimationFrame(() => {
      const r2 = requestAnimationFrame(() => setGrow(true));
      return () => cancelAnimationFrame(r2);
    });
    return () => cancelAnimationFrame(r1);
  }, [inView]);

  const jump = useCountUp(results.gradeJump.value, 1800, grow, 0);

  return (
    <section
      ref={ref}
      className="py-14 md:py-20 bg-brand-ink text-brand-cream relative overflow-hidden"
      aria-labelledby="stats-heading"
    >
      <div className="absolute inset-0 ring-dots opacity-20" aria-hidden="true" />
      <div className="container relative">
        <div className="max-w-none">
          <div className="text-brand-sun text-sm font-semibold uppercase tracking-widest flex items-center gap-2">
            <TrendingUp className="w-4 h-4" aria-hidden="true" /> {results.eyebrow}
          </div>
          <h2 id="stats-heading" className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl whitespace-normal md:whitespace-nowrap">
            Results that <span className="italic text-brand-sun">speak</span> for themselves.
          </h2>
          <p className="mt-3 text-brand-cream/70 text-base">{results.intro}</p>
        </div>

        <div className="mt-10 grid lg:grid-cols-3 gap-5 md:gap-6 items-stretch">
          {results.comparisons.map((c) => (
            <ComparisonBars key={c.id} item={c} inView={inView} />
          ))}

          {/* Grade jump highlight */}
          <div className="rounded-3xl bg-brand-sun text-brand-ink p-6 md:p-7 flex flex-col justify-between">
            <div className="text-xs uppercase tracking-widest font-bold">Average grade improvement</div>
            <div className="my-4">
              <span className="font-display text-7xl md:text-8xl leading-none">+{jump}</span>
              <span className="ml-2 font-display text-2xl md:text-3xl align-middle">
                {results.gradeJump.label}
              </span>
            </div>
            <p className="text-sm text-brand-ink/80 leading-snug">
              {results.gradeJump.suffix}
            </p>
          </div>
        </div>

        <p className="mt-6 text-xs text-brand-cream/50">{results.footnote}</p>
      </div>
    </section>
  );
}
